import { useState, useEffect, useRef } from 'react';
import Delete from '@monofront/design-system/visuals/Icons/Old/Delete';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import { useOnClickOutside } from '@monofront/business/hooks';
import ButtonShowCount from '@components/ButtonShowCount';
import { stopEventPropagation } from '@utils/stopEventPropagation';
import Subtract from '@monofront/design-system/visuals/Icons/Subtract';
import Add from '@monofront/design-system/visuals/Icons/Add';
import { useAlcoholEligibilityContext } from '@context/AlcoholEligibilityContext';

interface IButtonCounterProps {
  currentQuantity: number;
  max?: number;
  editCount?: (oldCount: number, newCount: number) => Promise<void>;
  hasEditViewOnly?: boolean;
  isLoading?: boolean;
  size?: 'medium' | 'large';
  isDisabled?: boolean;
  children: React.ReactNode;
  eligibilityShouldBeVerified?: boolean;
  UNSAFE_className?: string;
  minimumProductQuantity?: number;
}

const ButtonCounter: React.FC<IButtonCounterProps> = ({
  children,
  currentQuantity = 0,
  max = 999,
  editCount = () => undefined,
  hasEditViewOnly = false,
  size = 'medium',
  isLoading = false,
  eligibilityShouldBeVerified = false,
  isDisabled = false,
  UNSAFE_className = '',
  minimumProductQuantity = 1,
}) => {
  // TODO: investigate if we still need this state https://gitlab.com/la-fourche/scrum/-/issues/2349
  const [count, setCount] = useState(currentQuantity);
  const tAddToCartButton = useTranslations('Actions.addToCartButton');
  const [isEditMode, setIsEditMode] = useState(
    count > 0 ? hasEditViewOnly : false,
  );
  const refContainer = useRef<HTMLDivElement>(null);
  const { alcoholEligibilityState } = useAlcoholEligibilityContext();

  useOnClickOutside(
    refContainer,
    () => !hasEditViewOnly && setIsEditMode(false),
  );

  useEffect(() => {
    setCount(currentQuantity);
  }, [currentQuantity]);

  useEffect(() => {
    if (count <= 0) {
      setIsEditMode(false);
    }
  }, [count]);

  const tErrors = useTranslations('Errors');

  const handleEditCount = async (oldCount: number, newCount: number) => {
    if (isLoading) {
      return;
    }

    if (newCount < 0) {
      newCount = 0;
    }

    try {
      await editCount(oldCount, newCount);
      !eligibilityShouldBeVerified &&
        alcoholEligibilityState.canBuyProduct &&
        setCount(newCount);
    } catch (error: any) {
      toast(tErrors('updateError'), {
        progressClassName: 'toast-error',
      });
    }
  };

  const handleClickAddToCart = (event) => {
    stopEventPropagation(event);
    handleEditCount(count, count + minimumProductQuantity);
    !eligibilityShouldBeVerified && setIsEditMode(true);
  };

  if (count === 0) {
    return (
      <>
        <button
          type="button"
          className={`tw-lf-button tw-lf-button-conversion ${
            size === 'large' ? 'tw-lf-button-lg' : 'tw-lf-button-md'
          } ${UNSAFE_className}`}
          onClick={handleClickAddToCart}
          disabled={isDisabled}
          data-testid="ButtonCounter-Button-1"
          aria-label={tAddToCartButton('addToCart')}
        >
          {children}
        </button>
      </>
    );
  }

  if (count > 0 && !isEditMode && !hasEditViewOnly) {
    return (
      <ButtonShowCount
        count={count}
        size={size}
        onClick={(event) => {
          stopEventPropagation(event);
          setIsEditMode(true);
        }}
        ariaLabel={tAddToCartButton('addToCart')}
      />
    );
  }

  return (
    <>
      <div className="container" ref={refContainer}>
        <div className="left-btn">
          <button
            type="button"
            className="tw-lf-button-conversion tw-lf-button-icon tw-lf-button-icon-md lg:tw-lf-button-icon-lg"
            aria-label={count === 1 ? tAddToCartButton('delete') : '-1'}
            onClick={(event) => {
              stopEventPropagation(event);
              const quantityToRemove =
                count === minimumProductQuantity ? minimumProductQuantity : 1;

              handleEditCount(count, count - quantityToRemove);
            }}
            data-testId="ButtonCounter-Button-remove-item"
          >
            {count === minimumProductQuantity ? (
              <Delete aria-hidden />
            ) : (
              <Subtract aria-hidden focusable={false} />
            )}
          </button>
        </div>
        <input readOnly value={count} data-testid="ButtonCounter-input-count" />
        <div className="right-btn">
          <button
            type="button"
            className="tw-lf-button-conversion tw-lf-button-icon tw-lf-button-icon-md lg:tw-lf-button-icon-lg"
            aria-label={tAddToCartButton(count === max ? 'maximum' : 'add')}
            onClick={(event) => {
              stopEventPropagation(event);

              if (count <= max) {
                handleEditCount(count, count + 1);
              }
            }}
            data-testId="ButtonCounter-Button-add-item"
            disabled={count === max}
          >
            {count === max ? (
              <span className="tw-font-body tw-text-xs">{`MAX`}</span>
            ) : (
              <Add aria-hidden focusable={false} />
            )}
          </button>
        </div>
      </div>
      <style jsx>{`
        .container {
          display: flex;
          align-items: center;
          position: relative;
          width: max-content;
        }

        input {
          -webkit-appearance: none;
          border: none;
          text-align: center;
          border-top: 1px solid var(--clr-primary-2);
          border-bottom: 1px solid var(--clr-primary-2);
          font-size: var(--font-size-2);
          font-weight: var(--font-weight-light);
          width: 136px;
          height: 52px;
          border-radius: 26px;
        }

        .left-btn,
        .right-btn {
          position: absolute;
        }

        .left-btn {
          left: 0;
        }

        .right-btn {
          right: 0;
        }

        @media (max-width: 800px) {
          input {
            height: 44px;
          }
        }
      `}</style>
    </>
  );
};

export default ButtonCounter;
